/**
 * 图片 File / Blob / DataURL 互转工具（统一图片选择器裁切前后使用）。
 */

import { MAX_IMAGE_UPLOAD_BYTES } from '../constants/base.contants';

/** 编辑源图片的最长边上限（像素），避免超大原图直接进入 Cropper。 */
export const EDIT_SOURCE_MAX_EDGE = 2048;

/** 编辑与最终输出统一使用 JPEG，避免大 PNG 在浏览器中反复编解码。 */
export const EDIT_IMAGE_MIME = 'image/jpeg';

/** 编辑源 JPEG 编码质量。 */
const EDIT_SOURCE_QUALITY = 0.92;

/** 降采样后的编辑源及原图尺寸。 */
export interface EditableImageSource {
    /** 降采样并转码后的编辑源图片。 */
    blob: Blob;
    /** 原图宽度（像素）。 */
    naturalWidth: number;
    /** 原图高度（像素）。 */
    naturalHeight: number;
}

/**
 * 加载图片元素，用于读取原图尺寸及降采样失败时的兜底绘制。
 * @param src - 图片 Object URL
 * @returns 加载完成的图片元素
 */
function loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('image-load-failed'));
        image.src = src;
    });
}

/**
 * 在解码阶段缩放图片，避免为原始分辨率分配完整位图。
 * @param file - 原始图片文件
 * @param targetWidth - 目标宽度
 * @param targetHeight - 目标高度
 * @returns 缩放后的位图；不支持时返回 null
 */
async function createResizedBitmap(
    file: File | Blob,
    targetWidth: number,
    targetHeight: number,
    isLandscape: boolean,
): Promise<ImageBitmap | null> {
    if (typeof window.createImageBitmap !== 'function') {
        return null;
    }
    try {
        const options = {
            ...(isLandscape ? { resizeWidth: targetWidth } : { resizeHeight: targetHeight }),
            resizeQuality: 'high',
            imageOrientation: 'from-image',
        } as unknown as ImageBitmapOptions;
        return await window.createImageBitmap(file, options);
    } catch (error) {
        console.error('create resized bitmap', error);
        return null;
    }
}

/**
 * 从常见图片文件头读取尺寸，避免为了计算降采样比例先完整解码原图。
 * @param file - 原始图片文件
 * @returns 图片尺寸；无法识别时返回 null
 */
async function readEncodedImageDimensions(
    file: File | Blob,
): Promise<{ width: number; height: number } | null> {
    const bytes = new Uint8Array(await file.slice(0, 512 * 1024).arrayBuffer());
    const type = file.type.toLowerCase();
    if (type === 'image/png' && bytes.length >= 24) {
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        return { width: view.getUint32(16), height: view.getUint32(20) };
    }
    if (type === 'image/jpeg' && bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
        let offset = 2;
        while (offset + 9 < bytes.length) {
            if (bytes[offset] !== 0xff) {
                offset += 1;
                continue;
            }
            const marker = bytes[offset + 1];
            offset += 2;
            if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) {
                continue;
            }
            const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
            if (segmentLength < 2 || offset + segmentLength > bytes.length) {
                break;
            }
            const isStartOfFrame =
                (marker >= 0xc0 && marker <= 0xc3) ||
                (marker >= 0xc5 && marker <= 0xc7) ||
                (marker >= 0xc9 && marker <= 0xcb) ||
                (marker >= 0xcd && marker <= 0xcf);
            if (isStartOfFrame) {
                return {
                    height: (bytes[offset + 3] << 8) | bytes[offset + 4],
                    width: (bytes[offset + 5] << 8) | bytes[offset + 6],
                };
            }
            offset += segmentLength;
        }
    }
    return null;
}

/**
 * 创建供图片编辑器使用的轻量图片源。
 * @param file - 用户选择的原始图片
 * @param maxEdge - 编辑源最长边上限
 * @returns 降采样后的图片源及原图尺寸
 */
export async function createEditableImageSource(
    file: File | Blob,
    maxEdge: number = EDIT_SOURCE_MAX_EDGE,
): Promise<EditableImageSource> {
    /** 原图临时地址，函数结束后释放。 */
    const sourceUrl = URL.createObjectURL(file);
    /** 解码后的缩放位图，使用后释放显存。 */
    let bitmap: ImageBitmap | null = null;
    try {
        const encodedDimensions = await readEncodedImageDimensions(file);
        let image = encodedDimensions ? null : await loadImageElement(sourceUrl);
        const naturalWidth = encodedDimensions?.width || image?.naturalWidth || 0;
        const naturalHeight = encodedDimensions?.height || image?.naturalHeight || 0;
        if (!naturalWidth || !naturalHeight) {
            throw new Error('image-dimensions-invalid');
        }
        const scale = Math.min(1, maxEdge / Math.max(naturalWidth, naturalHeight));
        const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
        const targetHeight = Math.max(1, Math.round(naturalHeight * scale));
        bitmap = await createResizedBitmap(
            file,
            targetWidth,
            targetHeight,
            naturalWidth >= naturalHeight,
        );
        if (!bitmap && !image) {
            image = await loadImageElement(sourceUrl);
        }

        const canvas = document.createElement('canvas');
        canvas.width = bitmap?.width || targetWidth;
        canvas.height = bitmap?.height || targetHeight;
        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('canvas-context-unavailable');
        }
        // 使用主题白色作为 JPEG 透明区域的背景，避免 PNG 转 JPEG 后出现黑底。
        const backgroundColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--fill-neutral-100')
            .trim();
        if (backgroundColor) {
            context.fillStyle = backgroundColor;
            context.fillRect(0, 0, canvas.width, canvas.height);
        }
        context.drawImage(bitmap || image!, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (value) => (value ? resolve(value) : reject(new Error('canvas-blob-failed'))),
                EDIT_IMAGE_MIME,
                EDIT_SOURCE_QUALITY,
            );
        });
        return { blob, naturalWidth, naturalHeight };
    } finally {
        bitmap?.close();
        URL.revokeObjectURL(sourceUrl);
    }
}

/**
 * 将图片 File 读成 DataURL（供 Cropper 预览）。
 * @param file - 本地或网络拉取后的图片文件
 * @returns DataURL 字符串
 */
export async function readImageFileAsDataUrl(file: File | Blob): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (event) => {
            resolve(String(event.target?.result ?? ''));
        };
        reader.onerror = (event) => {
            reject(event);
        };
        reader.readAsDataURL(file);
    });
}

/**
 * 将 Blob 包装为 File（保留 MIME；可指定文件名）。
 * @param blob - 二进制图片
 * @param fileName - 文件名（默认按 MIME 生成）
 */
export function blobToImageFile(blob: Blob, fileName?: string): File {
    // MIME，缺省按 jpeg（fetch-from-url 约定返回 JPEG）
    const mime = (blob.type || 'image/jpeg').toLowerCase();
    // 扩展名
    const ext = mime.includes('png')
        ? 'png'
        : mime.includes('webp')
          ? 'webp'
          : mime.includes('gif')
            ? 'gif'
            : 'jpg';
    // 最终文件名
    const name = (fileName && fileName.trim()) || `image-${Date.now()}.${ext}`;
    return new File([blob], name, { type: mime });
}

/**
 * 判断图片文件是否满足最终上传大小限制。
 * @param file - 待校验的图片 Blob 或 File
 * @returns 文件大小不超过 500KB 时返回 true
 */
export function isImageFileWithinLimit(file: Blob): boolean {
    return file.size <= MAX_IMAGE_UPLOAD_BYTES;
}

/**
 * 从 DataURL 头解析 MIME（如 `image/jpeg`）；解析失败返回空串。
 * @param dataUrl - `data:image/...;base64,...`
 */
export function mimeFromDataUrl(dataUrl: string): string {
    const head = String(dataUrl || '').split(';base64,')[0] || '';
    const mime = head.includes(':') ? head.split(':')[1] : '';
    return mime || '';
}

/**
 * 估算 DataURL 体积（KB）。用于裁切后压缩决策。
 * @param dataUrl - base64 DataURL
 * @param mime - 与 DataURL 一致的 MIME
 */
export function estimateDataUrlSizeKb(dataUrl: string, mime: string): number {
    let str = dataUrl.replace(`data:${mime};base64,`, '');
    const equalIndex = str.indexOf('=');
    if (equalIndex > 0) {
        str = str.substring(0, equalIndex);
    }
    const strLength = str.length;
    const fileLength = strLength - (strLength / 8) * 2;
    const size = (fileLength / 1024).toFixed(2);
    const sizeStr = `${size}`;
    const index = sizeStr.indexOf('.');
    const dou = sizeStr.substr(index + 1, 2);
    if (dou === '00') {
        return Number(sizeStr.substring(0, index) + sizeStr.substr(index + 3, 2));
    }
    return Number(size);
}

/**
 * 将 Shell2 i18n 语言码映射为 Pixabay `lang` 参数。
 * @param appLang - 如 `CN` / `EN` / `IT` / `SP`
 * @returns Pixabay 支持的小写语言码
 */
export function mapAppLangToPixabayLang(appLang?: string): string {
    const key = String(appLang || '')
        .trim()
        .toUpperCase();
    const table: Record<string, string> = {
        CN: 'zh',
        ZH: 'zh',
        EN: 'en',
        IT: 'it',
        SP: 'es',
        ES: 'es',
        DE: 'de',
        FR: 'fr',
        PT: 'pt',
        SL: 'en',
    };
    return table[key] || 'en';
}

/** Pixabay 搜图可用语言选项，value 为 Pixabay 接口语言码。 */
export interface PixabayLanguageOption {
    /** Pixabay 接口语言码 */
    value: string;
    /** 下拉框展示的语言名称 */
    label: string;
}

/** Pixabay 搜图默认语言。 */
export const DEFAULT_PIXABAY_LANGUAGE = 'it';

/** Pixabay 支持的完整语言列表，顺序与接口文档保持一致。 */
export const PIXABAY_LANGUAGE_OPTIONS: PixabayLanguageOption[] = [
    { value: 'cs', label: 'Čeština' },
    { value: 'da', label: 'Dansk' },
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'id', label: 'Bahasa Indonesia' },
    { value: 'it', label: 'Italiano' },
    { value: 'hu', label: 'Magyar' },
    { value: 'nl', label: 'Nederlands' },
    { value: 'no', label: 'Norsk' },
    { value: 'pl', label: 'Polski' },
    { value: 'pt', label: 'Português' },
    { value: 'ro', label: 'Română' },
    { value: 'sk', label: 'Slovenčina' },
    { value: 'fi', label: 'Suomi' },
    { value: 'sv', label: 'Svenska' },
    { value: 'tr', label: 'Türkçe' },
    { value: 'vi', label: 'Tiếng Việt' },
    { value: 'th', label: 'ไทย' },
    { value: 'bg', label: 'Български' },
    { value: 'ru', label: 'Русский' },
    { value: 'el', label: 'Ελληνικά' },
    { value: 'ja', label: '日本語' },
    { value: 'ko', label: '한국어' },
    { value: 'zh', label: '中文' },
];

/**
 * 从 Pixabay hit 取用于 `fetch-from-url` 的地址：优先大图，其次中图，再缩略图。
 * @param hit - 搜图命中项
 */
export function resolvePixabayFetchUrl(hit: {
    largeImageURL?: string;
    webformatURL?: string;
    previewURL?: string;
}): string {
    return (
        String(hit?.largeImageURL || '').trim() ||
        String(hit?.webformatURL || '').trim() ||
        String(hit?.previewURL || '').trim()
    );
}
